import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Sparkles, Loader2, Tag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { MotorcycleIcon } from "./icons/VehicleIcons";
import BrandCombobox from "./listing/BrandCombobox";
import { MOTO_BRANDS, MOTORCYCLE_VEHICLE_TYPES, BRAND_MODELS } from "../lib/listingConstants";
import api from "../lib/api";

const PRICE_TIERS_MIN = ["500", "1000", "3000", "5000", "10000", "20000"];
const PRICE_TIERS_MAX = ["1000", "3000", "5000", "10000", "20000", "50000"];
// Feb-2026 (User-Wunsch) — Hero-Suche um Erstzulassung & Kilometer erweitert.
const YEAR_TIERS = ["2024", "2022", "2020", "2018", "2015", "2010", "2005"];
const MILEAGE_TIERS = ["5000", "10000", "20000", "30000", "50000", "75000", "100000"];

const fmt = (n) => Number(n).toLocaleString("de-DE");

// Zwei Tabs: „Fahrzeug-Suche" (Filter-Formular) und „Fahrzeug-Anbieten"
// (CTA → direkt zur Inserieren-Seite). Die Markenliste nutzt die gleiche,
// vollständige Quelle wie das Inserate-Erfassungsformular (MOTO_BRANDS).
const TABS = [
    { key: "vehicle", category: "motorcycle", vehicleType: null, brands: MOTO_BRANDS, icon: MotorcycleIcon, tKey: "search_hero.tab_search" },
    { key: "offer", icon: Tag, tKey: "search_hero.tab_offer" },
];

export default function SearchHero({ defaultTab = "vehicle" }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tabKey, setTabKey] = useState(defaultTab);
    const [brand, setBrand] = useState("any");
    const [vehicleType, setVehicleType] = useState("any");
    const [model, setModel] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [minYear, setMinYear] = useState("any");
    const [maxMileage, setMaxMileage] = useState("any");
    const [postal, setPostal] = useState("");
    // Mai-2026 (User-Wunsch 21 May Iteration 9) — KI-Suche im Hero auf Homepage,
    // verfügbar für alle 3 Tabs (Motorrad, Motorroller, Fahrrad).
    const [nlqInput, setNlqInput] = useState("");
    const [nlqLoading, setNlqLoading] = useState(false);

    const activeTab = useMemo(() => TABS.find((x) => x.key === tabKey) || TABS[0], [tabKey]);
    const brands = activeTab.brands || MOTO_BRANDS;
    // Modell-Vorschläge abhängig von der gewählten Marke (Free-Text bleibt erlaubt).
    const modelSuggestions = useMemo(
        () => (brand && brand !== "any" && BRAND_MODELS[brand]) ? BRAND_MODELS[brand] : [],
        [brand],
    );

    const minOptions = useMemo(
        () => [{ v: "any", l: t("search_hero.any") }, ...PRICE_TIERS_MIN.map((v) => ({ v, l: `${t("search_hero.from")} ${fmt(v)} €` }))],
        [t],
    );
    const maxOptions = useMemo(
        () => [{ v: "any", l: t("search_hero.any") }, ...PRICE_TIERS_MAX.map((v) => ({ v, l: `${t("search_hero.to")} ${fmt(v)} €` }))],
        [t],
    );

    const handleSearch = () => {
        const params = new URLSearchParams({ category: activeTab.category });
        if (activeTab.vehicleType) params.set("vehicle_type", activeTab.vehicleType);
        if (brand && brand !== "any") params.set("brand", brand);
        if (vehicleType && vehicleType !== "any") params.set("vehicle_type", vehicleType);
        if (model.trim()) params.set("model", model.trim());
        if (minPrice && minPrice !== "any") params.set("min_price", minPrice);
        if (maxPrice && maxPrice !== "any") params.set("max_price", maxPrice);
        if (minYear && minYear !== "any") params.set("min_year", minYear);
        if (maxMileage && maxMileage !== "any") params.set("max_mileage", maxMileage);
        if (postal) params.set("postal", postal);
        navigate(`/search?${params.toString()}`);
    };

    // Mai-2026 (User-Wunsch 21 May Iteration 9) — KI-Suche absenden:
    // schickt Free-Text an /listings/ai-search-nlq, lässt Gemini Marke, Modell,
    // Hubraum, Preis usw. extrahieren und navigiert mit allen erkannten
    // Filtern + Resttext zur /search-Seite.
    const handleNlqSearch = async () => {
        const query = nlqInput.trim();
        if (query.length < 3) {
            toast.error("Bitte mindestens 3 Zeichen für die KI-Suche eingeben.");
            return;
        }
        setNlqLoading(true);
        try {
            const { data } = await api.post("/listings/ai-search-nlq", { query });
            const f = data?.filters || {};
            const params = new URLSearchParams({ category: activeTab.category });
            if (activeTab.vehicleType) params.set("vehicle_type", activeTab.vehicleType);
            Object.entries(f).forEach(([k, v]) => {
                if (k === "category") return;
                if (v === null || v === undefined || v === "") return;
                params.set(k, String(v));
            });
            if (data.q_residual) params.set("q", data.q_residual);
            else params.set("q", query);
            const applied = Object.keys(f).length + (data.q_residual ? 1 : 0);
            if (data.mocked) {
                toast.warning("KI nicht erreichbar — nur Volltext-Suche aktiv.", { duration: 4000 });
            } else if (applied > 0) {
                toast.success(`${applied} Filter aus deiner Eingabe übernommen.`);
            }
            navigate(`/search?${params.toString()}`);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "KI-Suche fehlgeschlagen.");
        } finally {
            setNlqLoading(false);
        }
    };

    return (
        <div className="bg-white shadow-lg border border-gray-100 rounded-md overflow-hidden" data-testid="search-hero-widget">
            <div className="flex border-b border-gray-200">
                {TABS.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = tabKey === tab.key;
                    // Primary tabs (moto + scooter) sit on equal footing.
                    // The bicycle tab is intentionally muted to signal a
                    // secondary segment without hiding it.
                    const baseClasses = "flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold uppercase tracking-wider transition-colors";
                    const activeClasses = "bg-[#FFCC00] text-[#111827] border-b-4 border-[#111827]";
                    const inactivePrimary = "bg-white text-gray-600 hover:bg-gray-50";
                    const inactiveSecondary = "bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600";
                    const inactive = tab.secondary ? inactiveSecondary : inactivePrimary;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => { setTabKey(tab.key); setBrand("any"); setModel(""); setVehicleType("any"); }}
                            className={`${baseClasses} ${isActive ? activeClasses : inactive}`}
                            data-testid={`tab-${tab.key}`}
                        >
                            <Icon className="w-5 h-5" />
                            <span className={tab.secondary && !isActive ? "hidden sm:inline" : ""}>{t(tab.tKey)}</span>
                        </button>
                    );
                })}
            </div>

            {tabKey === "offer" ? (
                <div className="p-8 md:p-14 text-center flex flex-col items-center" data-testid="hero-offer-cta">
                    <div className="w-14 h-14 rounded-full bg-[#FFCC00] flex items-center justify-center mb-5">
                        <Tag className="w-7 h-7 text-[#111827]" />
                    </div>
                    <h3 className="font-display font-black text-2xl md:text-3xl text-[#111827] mb-2">
                        {t("search_hero.offer_headline")}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 max-w-xl mb-6">
                        {t("search_hero.offer_text")}
                    </p>
                    <Button
                        onClick={() => navigate("/create-listing")}
                        className="h-14 px-8 bg-[#111827] hover:bg-black text-white font-display font-black text-lg rounded-sm tracking-wide"
                        data-testid="offer-create-listing-btn"
                    >
                        {t("search_hero.offer_cta")} <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            ) : (
            <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-4">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{t("search_hero.brand")}</label>
                    <BrandCombobox
                        value={brand === "any" ? "" : brand}
                        onValueChange={(v) => { setBrand(v || "any"); setModel(""); }}
                        groups={[{ items: brands.map((b) => ({ value: b })) }]}
                        placeholder={t("search_hero.brand_any")}
                        searchPlaceholder="Marke suchen oder eingeben…"
                        clearLabel={t("search_hero.brand_any")}
                        testId="select-brand"
                    />
                </div>

                <div className="md:col-span-3">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{t("search_hero.price_from")}</label>
                    <Select value={minPrice} onValueChange={setMinPrice}>
                        <SelectTrigger className="h-12 rounded-sm border-gray-300" data-testid="select-min-price" aria-label="Mindestpreis wählen">
                            <SelectValue placeholder={t("search_hero.any")} />
                        </SelectTrigger>
                        <SelectContent>
                            {minOptions.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="md:col-span-3">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{t("search_hero.price_to")}</label>
                    <Select value={maxPrice} onValueChange={setMaxPrice}>
                        <SelectTrigger className="h-12 rounded-sm border-gray-300" data-testid="select-max-price" aria-label="Höchstpreis wählen">
                            <SelectValue placeholder={t("search_hero.any")} />
                        </SelectTrigger>
                        <SelectContent>
                            {maxOptions.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="md:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{t("search_hero.postal")}</label>
                    <Input
                        value={postal}
                        onChange={(e) => setPostal(e.target.value)}
                        placeholder={t("search_hero.postal_placeholder")}
                        className="h-12 rounded-sm border-gray-300"
                        data-testid="input-postal"
                    />
                </div>

                {/* Feb-2026 (User-Wunsch) — zweite Reihe: Kategorie, Modell, Erstzulassung ab, Kilometer bis */}
                <div className="md:col-span-3">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{t("search_hero.category")}</label>
                    <Select value={vehicleType} onValueChange={setVehicleType}>
                        <SelectTrigger className="h-12 rounded-sm border-gray-300" data-testid="select-category" aria-label="Fahrzeugkategorie wählen">
                            <SelectValue placeholder={t("search_hero.category_any")} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">{t("search_hero.category_any")}</SelectItem>
                            {MOTORCYCLE_VEHICLE_TYPES.map((vt) => <SelectItem key={vt} value={vt}>{vt}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="md:col-span-3">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{t("search_hero.model")}</label>
                    <Input
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        placeholder={t("search_hero.model_placeholder")}
                        className="h-12 rounded-sm border-gray-300"
                        list="hero-model-suggestions"
                        data-testid="input-model"
                    />
                    <datalist id="hero-model-suggestions">
                        {modelSuggestions.map((m) => <option key={m} value={m} />)}
                    </datalist>
                </div>

                <div className="md:col-span-3">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{t("search_hero.first_reg")}</label>
                    <Select value={minYear} onValueChange={setMinYear}>
                        <SelectTrigger className="h-12 rounded-sm border-gray-300" data-testid="select-min-year" aria-label="Erstzulassung ab wählen">
                            <SelectValue placeholder={t("search_hero.any")} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">{t("search_hero.any")}</SelectItem>
                            {YEAR_TIERS.map((y) => <SelectItem key={y} value={y}>{`${t("search_hero.from")} ${y}`}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="md:col-span-3">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{t("search_hero.mileage_to")}</label>
                    <Select value={maxMileage} onValueChange={setMaxMileage}>
                        <SelectTrigger className="h-12 rounded-sm border-gray-300" data-testid="select-max-mileage" aria-label="Maximale Laufleistung wählen">
                            <SelectValue placeholder={t("search_hero.any")} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">{t("search_hero.any")}</SelectItem>
                            {MILEAGE_TIERS.map((km) => <SelectItem key={km} value={km}>{`${t("search_hero.to")} ${fmt(km)} km`}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="md:col-span-12 mt-2">
                    <Button
                        onClick={handleSearch}
                        className="w-full h-14 bg-[#111827] hover:bg-black text-white font-display font-black text-lg rounded-sm tracking-wide"
                        data-testid="search-submit-btn"
                    >
                        <Search className="w-5 h-5 mr-2" /> {t("search_hero.search")}
                    </Button>
                </div>

                {/* Mai-2026 (User-Wunsch 21 May Iteration 9) — KI-Suche-Zeile, für alle 3 Tabs (Motorrad / Motorroller / Fahrrad). */}
                <div className="md:col-span-12 mt-1 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-300 rounded-sm p-3" data-testid="hero-nlq-row">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-amber-700" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">KI-Suche · in eigenen Worten beschreiben</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                            value={nlqInput}
                            onChange={(e) => setNlqInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleNlqSearch(); } }}
                            placeholder={"z. B. BMW R 1250 GS Bj 2020 mit Koffer unter 14.000 €"}
                            className="h-11 rounded-sm border-amber-300 flex-1 bg-white"
                            data-testid="hero-nlq-input"
                        />
                        <Button
                            type="button"
                            onClick={handleNlqSearch}
                            disabled={nlqLoading || nlqInput.trim().length < 3}
                            className="h-11 px-5 bg-[#111827] hover:bg-black text-white font-bold uppercase tracking-wider rounded-sm sm:w-44"
                            data-testid="hero-nlq-submit"
                        >
                            {nlqLoading
                                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sucht…</>
                                : <><Sparkles className="w-4 h-4 mr-1.5" /> KI-Suche</>}
                        </Button>
                    </div>
                    <p className="text-[10px] text-black mt-1.5">Gemini erkennt Marke, Modell, Preis, Region, Baujahr, Hubraum, Leistung u. v. m. — Rest wird als Volltext gesucht.</p>
                </div>
            </div>
            )}
        </div>
    );
}
